/**
 * Fullstory User Identification
 *
 * Wraps FS('setIdentity') and FS('setIdentity', {anonymous: true}) for the
 * marketing site. Identification fires primarily after Marketo form submit;
 * before that, the visitor is anonymous and we still set user properties
 * (landing page, UTM, referral source) per universal-data-scoping Rule 5
 * — those properties transfer automatically when the user later identifies.
 *
 * IMPORTANT: never pass raw email as uid. Use the Marketo lead id, the
 * hashed-email token, or any other internal identifier. Email goes in
 * properties under `email` (which FS will mask in replay if classified
 * as PII via fs-mask / data-fs-classification).
 *
 * Coordinates with consent.ts: identification calls are no-ops when
 * consent has been revoked. The wiring lands in Phase 8 — for now this
 * module just wraps the API and respects FS_CONFIG.features.userIdentification.
 */

import { FS_CONFIG, PROPERTY_TYPES } from './config';
import { setUserProperties } from './properties';

declare global {
  interface Window {
    FS: (method: string, args?: any) => void;
  }
}

// ─── User property shape (mktg-site specific) ───────────────────────────

/**
 * Properties we capture for marketing-site visitors. Covers both
 * post-form-submit identified leads and pre-identification anonymous
 * browsing context.
 */
export interface MarketingUserProperties {
  // Lead/account
  leadId?: string;
  leadStatus?: 'unknown' | 'mql' | 'sql' | 'opportunity' | 'customer';
  email?: string;          // mask via fs-mask on the input itself
  firstName?: string;
  lastName?: string;

  // Firmographic / role
  company?: string;
  companySize?: string;    // '1-50' | '51-200' | '201-1000' | '1000+'
  industry?: string;
  role?: string;           // e.g. 'marketing', 'product', 'engineering'
  jobTitle?: string;

  // Marketing source (works for anonymous users too — see Rule 5)
  landingPage?: string;
  referralSource?: string;
  campaign?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
}

const STR = PROPERTY_TYPES.STRING;

/**
 * Schema that pairs with MarketingUserProperties. Sent to FS so user-property
 * types are preserved in the analytics warehouse.
 */
export const USER_PROPERTY_SCHEMA = {
  leadId:         { type: STR },
  leadStatus:     { type: STR },
  email:          { type: STR },
  firstName:      { type: STR },
  lastName:       { type: STR },
  company:        { type: STR },
  companySize:    { type: STR },
  industry:       { type: STR },
  role:           { type: STR },
  jobTitle:       { type: STR },
  landingPage:    { type: STR },
  referralSource: { type: STR },
  campaign:       { type: STR },
  utmSource:      { type: STR },
  utmMedium:      { type: STR },
  utmCampaign:    { type: STR },
  utmContent:     { type: STR },
  utmTerm:        { type: STR },
};

// ─── Public API ─────────────────────────────────────────────────────────

export interface IdentifyArgs {
  /** Internal identifier — Marketo lead id, hashed email, etc. NEVER raw email. */
  uid: string;
  properties?: MarketingUserProperties;
}

/**
 * Identify a user in Fullstory. Call after Marketo form submit (or any other
 * point where you've reliably resolved the visitor to a known lead/account).
 *
 * @example
 *   identifyUser({
 *     uid: marketoLeadId,
 *     properties: {
 *       leadStatus: 'mql',
 *       email: leadEmail,
 *       company: 'Acme',
 *       industry: 'SaaS',
 *       role: 'marketing',
 *     },
 *   });
 */
export function identifyUser({ uid, properties }: IdentifyArgs): void {
  if (!FS_CONFIG.features.userIdentification) return;
  if (typeof window === 'undefined' || !window.FS) {
    if (!FS_CONFIG.isProduction) {
      console.warn('[FS] window.FS not available; identifyUser dropped:', uid);
    }
    return;
  }
  if (!uid) {
    if (!FS_CONFIG.isProduction) {
      console.warn('[FS] identifyUser called without uid — refusing to identify');
    }
    return;
  }

  window.FS('setIdentity', {
    uid,
    properties: properties || {},
    schema: { properties: USER_PROPERTY_SCHEMA },
  });

  if (!FS_CONFIG.isProduction) {
    console.debug('[FS identify]', uid, properties || {});
  }
}

/**
 * Switch the current session back to anonymous. Use on logout, on consent
 * revocation, or anywhere the user explicitly disowns the previous identity.
 * Subsequent events fire under a fresh anonymous id.
 */
export function anonymizeUser(): void {
  if (!FS_CONFIG.features.userIdentification) return;
  if (typeof window === 'undefined' || !window.FS) return;

  window.FS('setIdentity', { anonymous: true });

  if (!FS_CONFIG.isProduction) {
    console.debug('[FS anonymize]');
  }
}

/**
 * Set marketing-source user properties for an anonymous visitor.
 *
 * Per universal-data-scoping Rule 5: anonymous users can have user
 * properties. They persist via the fs_uid first-party cookie and transfer
 * to the identified user when identifyUser() is later called.
 *
 * Call this from a top-level layout or gatsby-browser.js after consent
 * is granted, ideally on the visitor's first page load.
 *
 * @example
 *   const params = new URLSearchParams(window.location.search);
 *   setAnonymousContext({
 *     landingPage: window.location.pathname,
 *     referralSource: document.referrer || undefined,
 *     utmSource:   params.get('utm_source')   || undefined,
 *     utmMedium:   params.get('utm_medium')   || undefined,
 *     utmCampaign: params.get('utm_campaign') || undefined,
 *     utmContent:  params.get('utm_content')  || undefined,
 *     utmTerm:     params.get('utm_term')     || undefined,
 *   });
 */
export function setAnonymousContext(properties: Pick<
  MarketingUserProperties,
  | 'landingPage'
  | 'referralSource'
  | 'campaign'
  | 'utmSource'
  | 'utmMedium'
  | 'utmCampaign'
  | 'utmContent'
  | 'utmTerm'
>): void {
  // Strip undefined keys so we don't write empty values that overwrite real ones.
  const clean: Record<string, string> = {};
  for (const [k, v] of Object.entries(properties)) {
    if (v != null && v !== '') clean[k] = String(v);
  }
  if (Object.keys(clean).length === 0) return;
  setUserProperties(clean);
}

/**
 * Extract UTM + landing context from window.location. Convenience helper
 * for callers that want to fire setAnonymousContext at mount time.
 *
 * Returns undefined when window isn't available (SSR).
 */
export function readBrowserContext(): Pick<
  MarketingUserProperties,
  'landingPage' | 'referralSource' | 'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmContent' | 'utmTerm'
> | undefined {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  return {
    landingPage: window.location.pathname,
    referralSource: document.referrer || undefined,
    utmSource:   params.get('utm_source')   || undefined,
    utmMedium:   params.get('utm_medium')   || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
    utmContent:  params.get('utm_content')  || undefined,
    utmTerm:     params.get('utm_term')     || undefined,
  };
}
